import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import './expandable-text.css';

const ANIM_FALLBACK_MS = 450;

const prefersReducedMotion = () => (
    typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
);

/*
 * La altura se anima a mano (px → px) porque `height: auto` no transiciona y el
 * line-clamp solo se quita/pone en los extremos para conservar los puntos suspensivos.
 */
export const ExpandableText = ({ text, className = '' }) => {
    const clipRef = useRef(null);
    const textRef = useRef(null);
    const collapsedHeightRef = useRef(0);
    const phaseRef = useRef(null);
    const fallbackRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [open, setOpen] = useState(false);
    const [clamped, setClamped] = useState(false);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || expanded) return undefined;
        const measure = () => {
            if (phaseRef.current) return;
            collapsedHeightRef.current = el.clientHeight;
            setClamped(el.scrollHeight - el.clientHeight > 1);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text, expanded]);

    useEffect(() => () => clearTimeout(fallbackRef.current), []);

    const finish = () => {
        const clip = clipRef.current;
        clearTimeout(fallbackRef.current);
        if (phaseRef.current === 'collapse') setExpanded(false);
        phaseRef.current = null;
        if (clip) clip.style.height = '';
    };

    const armFallback = () => {
        clearTimeout(fallbackRef.current);
        fallbackRef.current = setTimeout(finish, ANIM_FALLBACK_MS);
    };

    useLayoutEffect(() => {
        const clip = clipRef.current;
        if (!expanded || phaseRef.current !== 'expand' || !clip || !textRef.current) return;
        clip.getBoundingClientRect();
        clip.style.height = `${textRef.current.scrollHeight}px`;
        armFallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const handleToggle = (event) => {
        event.stopPropagation();
        const clip = clipRef.current;
        const next = !open;
        setOpen(next);
        if (!clip || prefersReducedMotion()) {
            setExpanded(next);
            return;
        }
        clip.style.height = `${clip.offsetHeight}px`;
        if (next) {
            phaseRef.current = 'expand';
            setExpanded(true);
            return;
        }
        phaseRef.current = 'collapse';
        clip.getBoundingClientRect();
        clip.style.height = `${collapsedHeightRef.current}px`;
        armFallback();
    };

    return (
        <div className={`expandable-text${className ? ` ${className}` : ''}`}>
            <div
                ref={clipRef}
                className="expandable-text__clip"
                onTransitionEnd={(e) => {
                    if (e.target === clipRef.current && e.propertyName === 'height') finish();
                }}
            >
                <p
                    ref={textRef}
                    className={`expandable-text__body${expanded ? ' is-expanded' : ''}`}
                >
                    {text}
                </p>
            </div>
            {(clamped || expanded) ? (
                <button
                    type="button"
                    className={`expandable-text__toggle${open ? ' is-open' : ''}`}
                    aria-expanded={open}
                    onClick={handleToggle}
                >
                    {open ? 'Ver menos' : 'Ver más'}
                    <ChevronDownIcon className="expandable-text__chevron" aria-hidden="true" />
                </button>
            ) : null}
        </div>
    );
};
